import { useEffect, useState, type ReactNode } from 'react';
import { Box, Group, Paper, SimpleGrid, Skeleton, Text } from '@mantine/core';
import type { OrderClientListItem } from '@swiftpay/shared';
import { clientOrdersApi } from '../../../api/clientOrders.js';
import { clientFinanceApi, type ClientFinanceSummary } from '../../../api/clientFinance.js';
import { BagIcon, WalletIcon, ChartIcon } from '../../../components/client/clientIcons.js';
import { Sparkline, weeklySums } from './sparkline.js';

const fmtRub = (n: number) => `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n)} ₽`;

function sumAmounts(orders: OrderClientListItem[]): number {
  return orders.reduce((acc, o) => acc + (Number.parseFloat(o.totalAmount) || 0), 0);
}

type CardData = {
  icon: ReactNode; label: string; value: string; sub: string;
  spark?: number[]; sparkColor?: string;
};

function StatCard({ c, compact }: { c: CardData; compact: boolean }) {
  return (
    <Paper radius={14} p={compact ? 'md' : 'lg'} style={{ background: '#fff', boxShadow: '0 1px 4px rgb(20 32 27 / 6%)' }}>
      <Group gap="sm" align="flex-end" justify="space-between" wrap="nowrap">
        <Group gap="sm" align="flex-start" wrap="nowrap">
          <Box
            style={{
              width: 44, height: 44, borderRadius: 12, background: '#E6F7F3',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0FAC97', flexShrink: 0,
            }}
          >
            {c.icon}
          </Box>
          <Box>
            <Text fz={14} c="#6B7570">{c.label}</Text>
            <Text fz={compact ? 26 : 30} fw={700} c="#14201B" lh={1.2}>{c.value}</Text>
            <Text fz={13} c="#6B7570">{c.sub}</Text>
          </Box>
        </Group>
        {c.spark && c.sparkColor && <Sparkline points={c.spark} color={c.sparkColor} />}
      </Group>
    </Paper>
  );
}

export function StatCards({ compact }: { compact: boolean }) {
  const [orders, setOrders] = useState<OrderClientListItem[] | null>(null);
  const [ordersFailed, setOrdersFailed] = useState(false);
  const [finance, setFinance] = useState<ClientFinanceSummary | null>(null);
  const [financeLoaded, setFinanceLoaded] = useState(false);

  useEffect(() => {
    clientOrdersApi.list().then(setOrders).catch(() => setOrdersFailed(true));
    clientFinanceApi.summary()
      .then((f) => { setFinance(f); setFinanceLoaded(true); })
      .catch(() => setFinanceLoaded(true)); // нет финансов — карточка долга покажет «—»
  }, []);

  const loading = (orders === null && !ordersFailed) || !financeLoaded;
  if (loading) {
    return (
      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
        <Skeleton h={100} radius={14} />
        <Skeleton h={100} radius={14} />
        <Skeleton h={100} radius={14} />
      </SimpleGrid>
    );
  }

  // Деградация по-карточно: падение orders-API не должно убирать карточку долга,
  // и наоборот. Каждая карточка строится из своего источника независимо.
  const shipped = orders?.filter((o) => o.status === 'shipped') ?? null;
  // «В работе» = все, кроме отгруженных и отменённых (черновики включаются) —
  // та же семантика активных, что на странице заказов.
  const active = orders?.filter((o) => o.status !== 'shipped' && o.status !== 'cancelled') ?? null;
  const debt = finance?.summary;
  const debtParts = debt
    ? [
        debt.debtTke ? `ТКЕ ${fmtRub(debt.debtTke)}` : null,
        debt.debtNpi ? `НПИ ${fmtRub(debt.debtNpi)}` : null,
      ].filter(Boolean)
    : [];

  const cards: CardData[] = ([
    shipped && {
      icon: <BagIcon size={22} />,
      label: 'Выполнено заказов',
      value: String(shipped.length),
      sub: `на сумму ${fmtRub(sumAmounts(shipped))}`,
      spark: weeklySums(shipped, (o) => o.shipmentDate ?? o.createdAt),
      sparkColor: '#0FAC97',
    },
    // Карточка долга видима всегда: отсутствие ИНН клиента в отчётах ДЗ — штатная
    // ситуация, а пропажа карточки читается как баг вёрстки (фидбек с прод-теста).
    // Клиент без строк в отчётах ДЗ видит честный «0 ₽», а не прочерк с «нет
    // данных» — отсутствие долга это нормальное состояние (фидбек Artem 05.08.2026).
    {
      icon: <WalletIcon size={22} />,
      label: 'Текущая задолженность',
      value: fmtRub(debt?.debtTotal ?? 0),
      sub: debtParts.length ? debtParts.join(' · ') : 'по данным последнего отчёта',
    },
    active && {
      icon: <ChartIcon size={22} />,
      label: 'Заказы в работе',
      value: String(active.length),
      sub: `на сумму ${fmtRub(sumAmounts(active))}`,
      spark: weeklySums(active, (o) => o.createdAt),
      sparkColor: '#3E8FF7',
    },
  ] as Array<CardData | null | undefined>).filter((c): c is CardData => !!c);

  if (cards.length === 0) return null;
  return (
    <SimpleGrid cols={{ base: 1, md: Math.min(cards.length, 3) }} spacing="md">
      {cards.map((c) => <StatCard key={c.label} c={c} compact={compact} />)}
    </SimpleGrid>
  );
}
